import React, { useState } from "react";
import { ChevronLeft } from "lucide-react";
import { IconButton, AppLink } from "../../Ui/index.jsx";
import { ATGLogo } from "../../Components/icons/icons.jsx";
import { entryFor } from "./navigation.js";

const COLLAPSED_KEY = "atg.nav.collapsed";

// One menu entry. It is a real link, so it can be opened in a new tab; a plain click moves inside the app.
function NavItem({ item, current, collapsed, onOpen }) {
  const Icon = item.icon;
  const open = (event) => {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
    event.preventDefault();
    onOpen(item.path);
  };
  // The dot says "something is waiting here" without a number: a count has to be read to learn whether it matters.
  const waiting = item.alert;
  return (
    <AppLink
      to={item.path}
      onClick={open}
      title={collapsed ? (waiting ? `${item.label} — waiting on you` : item.label) : undefined}
      aria-label={collapsed ? item.label : undefined}
      aria-current={current ? "page" : undefined}
      className={"Atg-Nav-Item" + (current ? " is-active" : "") + (item.urgent ? " is-urgent" : "")}
    >
      <span className="Atg-Nav-Icon"><Icon /></span>
      <span className="Atg-Nav-Label">{item.label}</span>
      {waiting && <span className="Atg-Nav-Alert" aria-label="Waiting on you" />}
    </AppLink>
  );
}

function readCollapsed() {
  try { return localStorage.getItem(COLLAPSED_KEY) === "1"; } catch { return false; }
}

// The left menu. `collapsed` survives a refresh (per browser); on a phone it slides in over the page (`mobileOpen`).
export function Sidebar({ nav, activePath, company, mobileOpen, onNavigate, onCloseMobile }) {
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const toggle = () => setCollapsed((was) => {
    const next = !was;
    try { localStorage.setItem(COLLAPSED_KEY, next ? "1" : "0"); } catch { /* private mode */ }
    return next;
  });
  const current = entryFor(activePath, nav.filter((entry) => !entry.isSection));

  return (
    <>
      {mobileOpen && <div className="Atg-Mobile-Backdrop" onClick={onCloseMobile} />}
      <aside className={"Atg-Sidebar" + (collapsed ? " Collapsed" : "") + (mobileOpen ? " MobileOpen" : "")}>
        <div className="Atg-Brand">
          <div className="Atg-Brand-Left">
            <ATGLogo company={company} />
            <span className="Atg-Brand-Name">Intranet</span>
          </div>
          <IconButton className="Atg-Collapse-Btn" label={collapsed ? "Expand sidebar" : "Collapse sidebar"} onClick={toggle}>
            <ChevronLeft size={18} />
          </IconButton>
        </div>
        <nav className="Atg-Nav">
          {nav.map((item) => item.isSection
            ? <div key={"section-" + item.label} className="Atg-Nav-Section" aria-hidden="true">{item.label}</div>
            : <NavItem key={item.path} item={item} current={item === current} collapsed={collapsed} onOpen={onNavigate} />)}
        </nav>
      </aside>
    </>
  );
}
